import { MAIN_SITE_ROUTES } from "@frontend/roz-sdk-core";
import { UiIcon } from "@frontend/roz-uikit";
import { Button } from "@frontend/roz-uikit/studio";
import { SearchIcon, XIcon } from "lucide-react";
import { Fragment, type MouseEvent, useState } from "react";

import { cn, navigateSdkHref } from "@/lib";
import { NavLink } from "@/primitives";
import { useSdk, useSdkConfig } from "@/provider";
import { useSheetStore } from "@/ui-state";

import { BottomLinks, JoinLinks, MiddleLinks, TopLinks } from "./menu-links";
import type { MenuLink } from "./menu-types";
import { ProjectsSection } from "./ProjectsSection";

const separator = "sdkui:h-px sdkui:w-full sdkui:bg-dark-10";
const menuItemBase =
  "sdkui:flex sdkui:w-full sdkui:items-center sdkui:gap-2 sdkui:rounded-md sdkui:px-2 sdkui:py-1.5 sdkui:text-dark-100 sdkui:text-base sdkui:leading-6 sdkui:transition-colors sdkui:hover:bg-dark-10";
const PROJECTS_SIDEBAR_BREAKPOINT = 768;
const collapsedSidebarClassName = "sdkui:w-full sdkui:md:w-78";
const expandedSidebarClassName = "sdkui:w-full sdkui:md:w-170 sdkui:lg:w-256";

export const Navigation = () => {
  const [activeMenuHref, setActiveMenuHref] = useState<string | null>(null);
  const [mobileProjectsOpen, setMobileProjectsOpen] = useState(false);
  const sdk = useSdk();
  const config = useSdkConfig();
  const isDarkTheme = config.theme === "dark";
  const { close } = useSheetStore();

  const isProjectsActive = activeMenuHref === MAIN_SITE_ROUTES.projects;

  const openProjectsSidebar = () => {
    setMobileProjectsOpen(false);
    setActiveMenuHref(MAIN_SITE_ROUTES.projects);
  };

  const openProjectsMobile = () => {
    setActiveMenuHref(null);
    setMobileProjectsOpen(true);
  };

  const handleClose = () => {
    close();
    setMobileProjectsOpen(false);
    setActiveMenuHref(null);
  };

  const handleMobileSearchOpen = () => {
    handleClose();
    navigateSdkHref(sdk, MAIN_SITE_ROUTES.search);
  };

  const renderLink = ({
    href,
    icon,
    text,
    "data-testid": dataTestId,
  }: MenuLink) => {
    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
      const isProjectsLink = href === MAIN_SITE_ROUTES.projects;
      const isProjectsSidebarMode =
        window.innerWidth >= PROJECTS_SIDEBAR_BREAKPOINT;

      if (isProjectsLink && isProjectsSidebarMode) {
        event.preventDefault();
        openProjectsSidebar();
      } else if (isProjectsLink) {
        event.preventDefault();
        openProjectsMobile();
      } else {
        handleClose();
      }
    };

    return (
      <NavLink
        className={cn(
          menuItemBase,
          isProjectsActive &&
            href === MAIN_SITE_ROUTES.projects &&
            "sdkui:bg-dark-10"
        )}
        href={href}
        onClick={handleClick}
        testId={dataTestId}
      >
        {icon}
        <span className="sdkui:min-w-0 sdkui:flex-1">{text}</span>
      </NavLink>
    );
  };

  const renderMenuList = () => (
    <div className="sdkui:flex sdkui:flex-col sdkui:gap-2">
      <div className="sdkui:flex sdkui:flex-col sdkui:gap-1">
        {TopLinks.map((link) => (
          <Fragment key={link.href}>{renderLink(link)}</Fragment>
        ))}
      </div>
      <div className={separator} />
      <div className="sdkui:flex sdkui:flex-col sdkui:gap-1">
        {MiddleLinks.map((link) => (
          <Fragment key={link.href}>{renderLink(link)}</Fragment>
        ))}
      </div>
      <div className={separator} />
      <div className="sdkui:flex sdkui:flex-col sdkui:gap-1">
        {BottomLinks.map((link) => (
          <Fragment key={link.href}>{renderLink(link)}</Fragment>
        ))}
      </div>
      <div className={separator} />
      <div className="sdkui:flex sdkui:flex-col sdkui:gap-1">
        {JoinLinks.map((link) => (
          <NavLink
            className={cn(menuItemBase, "sdkui:gap-2.5")}
            href={link.href}
            key={link.href}
            onClick={handleClose}
            testId={link["data-testid"]}
          >
            <span className="sdkui:flex sdkui:size-5 sdkui:items-center sdkui:justify-center sdkui:text-primary">
              <UiIcon className="sdkui:size-4" name="logo" />
            </span>
            <span>{link.text}</span>
          </NavLink>
        ))}
      </div>
    </div>
  );

  if (mobileProjectsOpen) {
    return (
      <div className="sdkui:fixed sdkui:inset-0 sdkui:z-10 sdkui:h-dvh sdkui:w-screen sdkui:min-w-screen sdkui:bg-dark-10">
        <ProjectsSection
          mobile
          onBack={() => {
            setMobileProjectsOpen(false);
            setActiveMenuHref(null);
          }}
        />
      </div>
    );
  }

  return (
    <div className="sdkui:relative sdkui:flex sdkui:h-full sdkui:w-full sdkui:max-md:flex-col sdkui:md:w-auto sdkui:md:items-start sdkui:md:justify-end">
      <button
        aria-label="Закрыть меню"
        className={cn(
          "sdkui:absolute sdkui:inset-0 sdkui:border-0 sdkui:p-0 sdkui:backdrop-blur-[20px] sdkui:max-md:hidden",
          isDarkTheme ? "sdkui:bg-dark-100/20" : "sdkui:bg-zinc-100/20"
        )}
        onClick={handleClose}
        type="button"
      />

      <div className="sdkui:relative sdkui:flex sdkui:h-full sdkui:w-full sdkui:max-md:flex-col sdkui:md:w-auto sdkui:md:items-start">
        <Button
          aria-label="Закрыть меню"
          className="sdkui:absolute sdkui:top-3 sdkui:right-full sdkui:z-10 sdkui:mr-3 sdkui:hidden sdkui:bg-white sdkui:text-dark-80 sdkui:hover:bg-white sdkui:md:flex"
          iconOnly
          onClick={handleClose}
          size="lg"
          variant="solidSecondary"
        >
          <XIcon />
        </Button>

        <div
          className={cn(
            "sdkui:flex sdkui:h-full sdkui:w-full sdkui:bg-white sdkui:max-md:min-h-dvh sdkui:max-md:flex-col sdkui:md:flex-none sdkui:md:overflow-hidden",
            isProjectsActive
              ? expandedSidebarClassName
              : collapsedSidebarClassName
          )}
        >
          <div
            className={cn(
              "sdkui:flex sdkui:w-full sdkui:flex-col sdkui:max-md:px-4 sdkui:max-md:pt-1 sdkui:max-md:pb-4",
              "sdkui:md:w-72 sdkui:md:shrink-0 sdkui:md:px-6 sdkui:md:py-6 sdkui:lg:w-82",
              isProjectsActive && "sdkui:md:max-lg:w-72 sdkui:lg:w-82"
            )}
          >
            <div className="sdkui:flex sdkui:items-center sdkui:gap-2 sdkui:px-1 sdkui:py-4 sdkui:md:hidden">
              <button
                aria-label="Открыть поиск"
                className="sdkui:flex sdkui:h-10 sdkui:min-w-0 sdkui:flex-1 sdkui:items-center sdkui:gap-2 sdkui:rounded-xl sdkui:border sdkui:border-zinc-100 sdkui:bg-white sdkui:px-3 sdkui:text-dark-60 sdkui:text-sm sdkui:shadow-none"
                onClick={handleMobileSearchOpen}
                type="button"
              >
                <SearchIcon className="sdkui:size-4 sdkui:shrink-0" />
                <span className="sdkui:min-w-0 sdkui:truncate">
                  Мероприятия, фильмы, лекторы
                </span>
              </button>

              <Button
                aria-label="Закрыть меню"
                className="sdkui:shrink-0 sdkui:text-dark-80"
                iconOnly
                onClick={handleClose}
                size="lg"
                variant="ghostPrimary"
              >
                <XIcon />
              </Button>
            </div>

            <div className="sdkui:flex-1 sdkui:overflow-y-auto">
              {renderMenuList()}
            </div>
          </div>

          <div className="sdkui:hidden sdkui:h-full sdkui:min-w-0 sdkui:flex-1 sdkui:md:block sdkui:md:max-lg:w-89 sdkui:md:max-lg:flex-none sdkui:md:max-lg:p-4 sdkui:md:max-lg:pl-0 sdkui:lg:w-177 sdkui:lg:flex-none sdkui:lg:p-6 sdkui:lg:pl-0">
            {isProjectsActive ? <ProjectsSection /> : null}
          </div>
        </div>
      </div>
    </div>
  );
};
